/**
 * The quotation document: one version of a builder's estimate, with the
 * versions before and after it, what changed, the scope against the brief,
 * deliverables by stage, the price (build + Twasta's service fee, and the
 * monthly price to use it), the standard payment terms and the client's
 * decision. Builders see it read-only. Doc: docs/marketplace_get_it_built.md
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { fmtDate, mktError, money } from '@/api/marketplace'
import { askRevision, declineQuote, getQuote, type Quote } from '@/api/marketplaceBuild'
import MarketplaceShell from '../MarketplaceShell'
import HostCallout from './HostCallout'
import PaymentStages from './PaymentStages'
import {
  briefItems, covers, coveredCount, daysLeft, ErrorPanel, hasMonthly, Loading, monthlyTotal, noReplyText, quoteLive,
  quoteStatus, weeksText,
} from './clientShared'

export default function QuotePage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [q, setQ] = useState<Quote | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<'idle' | 'revise' | 'decline'>('idle')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<null | 'revised' | 'declined'>(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null); setMode('idle'); setDone(null)
    try {
      setQ(await getQuote(Number(id)))
    } catch (e) {
      setError(mktError(e, 'This quote could not be loaded.').message)
    } finally {
      setLoading(false)
    }
  }, [id])
  useEffect(() => { load() }, [load])

  const sendRevision = async () => {
    if (!q) return
    if (!note.trim()) { toast.error('Say what should change.'); return }
    setBusy(true)
    try {
      const r = await askRevision(q.id, note.trim())
      setQ((cur) => ({ ...(cur as Quote), ...r })); setDone('revised'); setMode('idle'); setNote('')
    } catch (e) {
      toast.error(mktError(e, 'The revision could not be asked.').message)
    } finally {
      setBusy(false)
    }
  }

  const decline = async () => {
    if (!q) return
    setBusy(true)
    try {
      const r = await declineQuote(q.id)
      setQ((cur) => ({ ...(cur as Quote), ...r })); setDone('declined'); setMode('idle')
    } catch (e) {
      toast.error(mktError(e, 'The quote could not be declined.').message)
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <MarketplaceShell><Loading text="Loading the quote…" /></MarketplaceShell>
  if (error || !q) return <MarketplaceShell><ErrorPanel message={error || 'This quote could not be loaded.'} onRetry={load} /></MarketplaceShell>

  const cur = q.currency
  const req = q.request
  const isClient = q.role !== 'builder'
  const items = briefItems(req)
  const versions = (q.versions || []).slice().sort((a, b) => a.version - b.version)
  const latest = versions[versions.length - 1]
  const prev = versions.filter((v) => v.version < q.version).pop()
  const st = quoteStatus(q)
  const live = quoteLive(q)
  const canOrder = isClient && !!(q.approver ?? req?.approver) && live
  const fee = q.stages.reduce((n, s) => n + (s.client?.service_fee_minor || 0), 0)
  const total = q.stages.reduce((n, s) => n + (s.client?.total_minor || s.amount_minor), 0)
  const feePct = q.terms?.client_fee_percent ?? q.stages[0]?.client?.service_fee_percent
  const mTotal = monthlyTotal(q.monthly)
  const left = daysLeft(q.valid_until)
  let cum = 0
  const targets = q.stages.map((s, i) => {
    if (i === 0) return 'On order'
    cum += s.weeks || 0
    return s.weeks ? `By week ${cum}` : '—'
  })

  const changes: { k: string; tone: string; t: string }[] = []
  if (prev) {
    if (prev.price_minor !== q.price_minor) changes.push({ k: 'Price', tone: q.price_minor < prev.price_minor ? 'ok' : 'warn', t: `${money(prev.price_minor, cur)} → ${money(q.price_minor, cur)}` })
    if ((prev.weeks || 0) !== (q.weeks || 0)) changes.push({ k: 'Time', tone: (q.weeks || 0) < (prev.weeks || 0) ? 'ok' : 'warn', t: `${weeksText(prev.weeks)} → ${weeksText(q.weeks)}` })
    if (!changes.length) changes.push({ k: 'Price & time', tone: '', t: 'Same price and time as version ' + prev.version + ' — see the scope and stages below for what was reworded.' })
  }

  const termsBlocks: { h: string; items: string[] }[] = q.terms ? [
    {
      h: 'Approving each stage',
      items: [
        `You have ${q.terms.approve_days} days to approve a stage or ask for changes.`,
        noReplyText(q.terms),
        'Nothing extra is built without your approval.',
      ],
    },
    {
      h: 'Paying',
      items: [
        'You pay Twasta, never the builder directly.',
        q.terms.collect_on === 'approve'
          ? 'Each share is taken from your organization wallet when you approve that stage.'
          : 'Each share is taken from your organization wallet when the builder submits that stage.',
        'Twasta pays the builder for a stage only after you approve it.',
      ],
    },
    {
      h: 'After sign-off',
      items: [
        `${q.terms.fix_window_days} days of free fixes for anything that doesn’t work as agreed.`,
        'Then updates and fixes are part of the monthly price.',
        'You can download all your data at any time.',
      ],
    },
    {
      h: 'Where it is built',
      items: [
        `In ${q.builder.name}’s own Twasta account, with sample data.`,
        `At sign-off it is installed in ${req?.organization || 'your organization'} — your own copy and your own database.`,
      ],
    },
  ] : []

  return (
    <MarketplaceShell>
      <div className="mkt-wrap" style={{ paddingTop: 22, paddingBottom: 64, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          {isClient
            ? <Link to={`/marketplace/build/requests/${q.request_id}`} style={{ fontSize: 14, textDecoration: 'none' }}>← {req?.title || 'Your request'} · all estimates</Link>
            : <Link to={`/marketplace/work/${q.request_id}`} style={{ fontSize: 14, textDecoration: 'none' }}>← Back to the request</Link>}
          {versions.length > 1 && (
            <div className="mkt-row" style={{ gap: 8 }}>
              <span className="mkt-faint">Version</span>
              <div role="group" aria-label="Quote version" style={{ display: 'inline-flex', background: 'var(--color-raised, var(--color-bg))', borderRadius: 8, padding: 4, gap: 4 }}>
                {versions.map((v) => {
                  const on = v.id === q.id
                  return (
                    <button key={v.id} type="button" className="btn btn-sm" aria-pressed={on} onClick={() => { if (!on) navigate(`/marketplace/build/quotes/${v.id}`) }}
                      style={on ? { background: 'var(--color-bg-secondary)', color: 'var(--color-text)', boxShadow: '0 1px 3px var(--color-shadow, rgba(0,0,0,.12))' } : { background: 'transparent', color: 'var(--color-text-secondary)' }}>
                      v{v.version}{v.id === latest?.id ? ' · latest' : ''}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <div className="mkt-split" style={{ gap: 20 }}>
          <article className="mkt-panel mkt-main" style={{ padding: 0, overflow: 'hidden', gap: 0 }}>
            <div style={{ padding: '26px 28px', borderBottom: '1px solid var(--color-border)', display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'space-between' }}>
              <div className="mkt-col" style={{ gap: 4 }}>
                <span className="mkt-pill info" style={{ alignSelf: 'flex-start' }}>Quotation</span>
                <h1 style={{ fontSize: 28 }}>{req?.title || 'Estimate'}</h1>
                <span className="mkt-muted" style={{ fontSize: 14 }}>
                  Quote <strong style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--color-text)' }}>#{q.id} · v{q.version}</strong>
                  {q.sent_at && <> · issued {fmtDate(q.sent_at)}</>}
                  {q.valid_until && <> · valid until <strong style={{ color: 'var(--color-text)' }}>{fmtDate(q.valid_until)}</strong></>}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16, fontSize: 14, minWidth: 280 }}>
                <div><div className="mkt-faint">From</div><strong>{q.builder.name}</strong>{q.builder.person && q.builder.person !== q.builder.name && <div className="mkt-muted">{q.builder.person}</div>}{q.builder.headline && <div className="mkt-faint">{q.builder.headline}</div>}</div>
                <div><div className="mkt-faint">For</div><strong>{req?.organization || 'Your organization'}</strong></div>
              </div>
            </div>

            {prev && (
              <div style={{ background: 'var(--color-accent-weak)', borderBottom: '1px solid var(--color-border)', padding: '14px 28px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
                <strong>What changed since version {prev.version}</strong>
                {changes.map((c) => (
                  <div key={c.k} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}><span className={`mkt-pill ${c.tone}`} style={{ minWidth: 74, textAlign: 'center' }}>{c.k}</span><span>{c.t}</span></div>
                ))}
              </div>
            )}
            {q.status === 'superseded' && latest && latest.id !== q.id && (
              <div className="mkt-banner warn" style={{ borderRadius: 0 }} role="status">
                <div><strong>This is an older version</strong>{q.builder.name} has sent version {latest.version}.</div>
                <Link className="btn btn-secondary btn-sm" to={`/marketplace/build/quotes/${latest.id}`}>See version {latest.version}</Link>
              </div>
            )}

            {q.note && (
              <section style={{ padding: '22px 28px', borderBottom: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <h2 style={{ fontSize: 18 }}>A note from {q.builder.name}</h2>
                <p className="mkt-muted" style={{ fontSize: 14, margin: 0, whiteSpace: 'pre-line' }}>{q.note}</p>
              </section>
            )}

            <section style={{ padding: '22px 28px', borderBottom: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <h2 style={{ fontSize: 18 }}>1. Scope — what will be built</h2>
              {items.length ? (
                <>
                  <p className="mkt-muted" style={{ fontSize: 14, margin: 0 }}>Priced against the brief: {coveredCount(q, items)} of {items.length} items included. Anything not listed here is a change.</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '6px 20px' }}>
                    {items.map((it) => {
                      const c = covers(q, it)
                      return (
                        <div key={it} style={{ display: 'flex', gap: 8, fontSize: 14, padding: '6px 0', borderBottom: '1px dashed var(--color-border)' }}>
                          <span aria-hidden="true" style={{ fontWeight: 700, color: c.included ? 'var(--color-success)' : 'var(--color-error)' }}>{c.included ? '✓' : '✕'}</span>
                          <span><span className="mkt-sr">{c.included ? 'Included: ' : 'Not included: '}</span>{it}{c.note && <span className="mkt-faint" style={{ display: 'block' }}>{c.note}</span>}</span>
                        </div>
                      )
                    })}
                  </div>
                </>
              ) : (
                <p className="mkt-muted" style={{ fontSize: 14, margin: 0 }}>The request had no itemised brief; the builder priced its description.</p>
              )}
            </section>

            <section style={{ padding: '22px 28px', borderBottom: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <h2 style={{ fontSize: 18 }}>2. Deliverables by stage</h2>
              <div className="mkt-scroll">
                <table className="mkt-table" style={{ minWidth: 640 }}>
                  <thead><tr><th>Stage</th><th>You approve</th><th>Target</th><th className="num">Share</th><th className="num">Amount</th></tr></thead>
                  <tbody>
                    {q.stages.map((s, i) => (
                      <tr key={s.key || i}>
                        <td><strong>{s.name}</strong></td>
                        <td>
                          {i === 0 ? <span className="mkt-muted">Paid when you order</span> : (
                            <>
                              {s.approves && <div>{s.approves}</div>}
                              {s.deliverables && <div className="mkt-muted" style={{ whiteSpace: 'pre-line' }}>{s.deliverables}</div>}
                              {!s.approves && !s.deliverables && <span className="mkt-muted">—</span>}
                            </>
                          )}
                        </td>
                        <td>{targets[i]}</td>
                        <td className="num">{s.pct}%</td>
                        <td className="num" style={{ fontWeight: 600 }}>{money(s.amount_minor, cur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section style={{ padding: '22px 28px', borderBottom: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <h2 style={{ fontSize: 18 }}>3. Price</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 }}>
                <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
                  <span className="mkt-pill info" style={{ alignSelf: 'flex-start' }}>To build — one time</span>
                  <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Build price (fixed)</span><strong>{money(q.price_minor, cur)}</strong></div>
                  <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Twasta service fee{feePct ? ` ${feePct}%` : ''}</span><span>{money(fee, cur)}</span></div>
                  <div className="mkt-row" style={{ justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: 8, fontSize: 16 }}><strong>Total over all stages</strong><strong>{money(total, cur)}</strong></div>
                  <span className="mkt-faint">Paid from your organization wallet. GST is added when you put money into the wallet, so there is no second GST line here.</span>
                </div>
                <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
                  <span className="mkt-pill ok" style={{ alignSelf: 'flex-start' }}>To use — every month, from install</span>
                  {hasMonthly(q.monthly) ? (
                    <>
                      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
                        <span className="mkt-muted">{q.monthly.basis === 'server' ? `Per server${q.monthly.size ? ` (size ${q.monthly.size})` : ''}` : 'Per person'}</span>
                        <strong>{money(q.monthly.price_minor, cur)}</strong>
                      </div>
                      {q.monthly.basis === 'person' && q.monthly.seats && mTotal !== null && (
                        <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">For {q.monthly.seats} {q.monthly.seats === 1 ? 'person' : 'people'}</span><strong>{money(mTotal, cur)} a month</strong></div>
                      )}
                    </>
                  ) : <span className="mkt-muted">No monthly price in this version.</span>}
                  <span className="mkt-faint">Includes hosting, updates and fixes. Paid from your wallet like any Marketplace app; change or cancel in My apps.</span>
                  <HostCallout currency={cur} />
                </div>
              </div>
            </section>

            <section style={{ padding: '22px 28px', borderBottom: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <h2 style={{ fontSize: 18 }}>4. Payment terms</h2>
              <p className="mkt-faint" style={{ margin: 0 }}>Twasta’s standard terms. You pay Twasta; Twasta pays {q.builder.name} after you approve each stage.</p>
              <PaymentStages stages={q.stages} rows={q.stages} amountMinor={q.price_minor} currency={cur} who={isClient ? 'client' : 'builder'} advanceRelease={q.terms?.advance_release} />
            </section>

            {termsBlocks.length > 0 && (
              <section style={{ padding: '22px 28px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
                {termsBlocks.map((t) => (
                  <div key={t.h} className="mkt-col" style={{ gap: 6 }}>
                    <h3 style={{ fontSize: 15 }}>{t.h}</h3>
                    <ul className="mkt-muted" style={{ margin: 0, paddingLeft: 18, fontSize: 14, display: 'flex', flexDirection: 'column', gap: 4 }}>{t.items.map((i) => <li key={i}>{i}</li>)}</ul>
                  </div>
                ))}
                {q.demos?.length > 0 && (
                  <div className="mkt-col" style={{ gap: 6 }}>
                    <h3 style={{ fontSize: 15 }}>Similar work</h3>
                    <ul className="mkt-muted" style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
                      {q.demos.map((d) => <li key={d.name}>{d.url ? <a href={d.url} target="_blank" rel="noopener noreferrer">{d.name}</a> : d.name}{d.note ? ` — ${d.note}` : ''}</li>)}
                    </ul>
                  </div>
                )}
              </section>
            )}
          </article>

          <aside className="mkt-aside" style={{ position: 'sticky', top: 76 }}>
            <div className="mkt-panel" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
                <strong style={{ fontSize: 17 }}>{isClient ? 'Your decision' : 'Your estimate'}</strong>
                <span className={`mkt-pill ${live ? 'warn' : st.tone}`}>{live && left !== null ? (left === 0 ? 'Valid today only' : `Valid ${left} more ${left === 1 ? 'day' : 'days'}`) : st.label}</span>
              </div>
              <div className="mkt-row" style={{ justifyContent: 'space-between', fontSize: 14 }}><span className="mkt-muted">To build</span><strong>{money(q.price_minor, cur)}</strong></div>
              <div className="mkt-row" style={{ justifyContent: 'space-between', fontSize: 14 }}><span className="mkt-muted">Live in</span><strong>{weeksText(q.weeks)}</strong></div>
              {q.advance_due && <div className="mkt-row" style={{ justifyContent: 'space-between', fontSize: 14 }}><span className="mkt-muted">{isClient ? 'Due today if you order' : 'Advance the client pays'}</span><strong>{money(isClient ? q.advance_due.total_minor : q.advance_minor, cur)}</strong></div>}

              {isClient ? (
                <>
                  {done === 'revised' && <div className="mkt-msg ok" role="status">Revision asked. {q.builder.name} will send version {q.version + 1}. This quote stays valid meanwhile.</div>}
                  {done === 'declined' && <div className="mkt-msg warn" role="status">Declined. {q.builder.name} has been told.</div>}
                  {live && mode === 'idle' && (
                    <>
                      {canOrder
                        ? <Link className="btn btn-primary" to={`/marketplace/build/order/${q.id}`}>Accept &amp; place order</Link>
                        : <span className="mkt-faint">Only your organization’s admins or project owners can place the order.</span>}
                      <button type="button" className="btn btn-secondary" onClick={() => setMode('revise')}>Ask for a revision</button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMode('decline')}>Decline this quote</button>
                    </>
                  )}
                  {mode === 'revise' && (
                    <div className="mkt-field">
                      <label htmlFor="qt-rev">What should change?</label>
                      <textarea id="qt-rev" rows={4} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} placeholder="For example: could the second stage include the parts screen? Could you start a week earlier?" />
                      <div className="mkt-row" style={{ gap: 8 }}>
                        <button type="button" className="btn btn-primary btn-sm" onClick={sendRevision} disabled={busy || !note.trim()}>{busy ? 'Sending…' : `Send to ${q.builder.name}`}</button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMode('idle')}>Cancel</button>
                      </div>
                    </div>
                  )}
                  {mode === 'decline' && (
                    <div className="mkt-col" style={{ gap: 8 }} role="alert">
                      <span style={{ fontSize: 14 }}><strong>Decline this quote?</strong> {q.builder.name} is told you won’t go ahead with it.</span>
                      <div className="mkt-row" style={{ gap: 8 }}>
                        <button type="button" className="btn btn-primary btn-sm" onClick={decline} disabled={busy}>{busy ? 'Declining…' : 'Yes, decline'}</button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMode('idle')}>Keep it</button>
                      </div>
                    </div>
                  )}
                  {!live && q.status === 'accepted' && <span className="mkt-faint">You ordered this estimate. Follow the work from My apps.</span>}
                  {!live && q.expired && (q.status === 'sent' || q.status === 'revision_asked') && <span className="mkt-faint">This quote has expired. Ask {q.builder.name} for a new version from your request’s messages.</span>}
                  <p className="mkt-faint" style={{ margin: 0 }}>Accepting takes you to place the order. Nothing is charged until you pay the advance.</p>
                </>
              ) : (
                <>
                  <span className="mkt-faint">This is the quote as the client sees it.</span>
                  <Link className="btn btn-secondary" to={`/marketplace/work/${q.request_id}`}>Open your estimate</Link>
                </>
              )}
            </div>

            {versions.length > 0 && (
              <div className="mkt-panel" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
                <strong>Quote history</strong>
                {req?.posted_at && <div style={{ display: 'flex', gap: 10 }}><span className="mkt-faint" style={{ width: 92, flex: '0 0 auto' }}>{fmtDate(req.posted_at)}</span><span>The request was posted</span></div>}
                {versions.map((v) => (
                  <div key={v.id} style={{ display: 'flex', gap: 10 }}>
                    <span className="mkt-faint" style={{ width: 92, flex: '0 0 auto' }}>{fmtDate(v.sent_at) || '—'}</span>
                    <span>
                      {v.id === q.id ? <strong>Version {v.version}</strong> : <Link to={`/marketplace/build/quotes/${v.id}`}>Version {v.version}</Link>}
                      {' — '}{money(v.price_minor, cur)}, {weeksText(v.weeks)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </aside>
        </div>
      </div>
    </MarketplaceShell>
  )
}
